/**
 * "Avtal under bearbetning" — contracts grouped by their first reminder.
 *
 * One row per contract (the contract's earliest applicable reminder), sorted
 * by next-closest date. A window switcher flips between:
 *
 *   Aktuella  — fire_at <= now, unacknowledged ("visas nu"). Sorted desc.
 *               Default tab — these are the items that need action now.
 *   Kommande  — fire_at in the future, unacknowledged. Sorted asc.
 *   Hanterade — acknowledged history. Sorted by acknowledgedAt desc.
 *
 * Reached from the Översikt page. Clicking a row deep-links to the contract
 * editor's Notifications tab (`?tab=paminnelser`), matching the bell.
 */

import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import {
  tenantApi,
  type ContractReminderWindow,
  type ContractWithFirstReminder,
  type NotificationKind,
} from '../api.js';
import { useTenantQuery } from '../query.js';
import { usePageTitle } from '../use-page-title.js';
import { useState } from 'react';

const HB = '#1A3D2B';

const KIND_LABELS: Record<NotificationKind, string> = {
  termination_notice: 'Uppsägning',
  negotiation_window: 'Förhandlingsfönster',
  lease_end: 'Hyresavtal löper ut',
  agreement_end: 'Förhandlingsordning löper ut',
  signature_overdue: 'Inväntar signering',
  custom: 'Påminnelse',
};

const CONTRACT_STATUS_LABEL: Record<string, string> = {
  draft: 'Utkast',
  pending: 'Klar för granskning',
  in_review: 'Granskas',
  sent: 'Skickat',
  viewed: 'Öppnat',
  awaiting_signature: 'Väntar på signering',
  signed: 'Signerat',
  archived: 'Arkiverat',
  cancelled: 'Avbrutet',
};

const CONTRACT_STATUS_CLASS: Record<string, string> = {
  draft: 'bg-stone-100 text-stone-700',
  pending: 'bg-blue-100 text-blue-800',
  in_review: 'bg-purple-100 text-purple-800',
  sent: 'bg-blue-100 text-blue-800',
  viewed: 'bg-blue-100 text-blue-800',
  awaiting_signature: 'bg-amber-100 text-amber-900',
  signed: 'bg-green-100 text-green-800',
  archived: 'bg-stone-200 text-stone-600',
  cancelled: 'bg-red-100 text-red-800',
};

const WINDOWS: Array<{
  id: ContractReminderWindow;
  label: string;
  empty: string;
}> = [
  {
    id: 'due',
    label: 'Aktuella',
    empty: 'Inga aktuella påminnelser just nu.',
  },
  {
    id: 'upcoming',
    label: 'Kommande',
    empty: 'Inga kommande påminnelser just nu.',
  },
  {
    id: 'past',
    label: 'Hanterade',
    empty: 'Inga hanterade påminnelser ännu.',
  },
];

function rowTitle(n: ContractWithFirstReminder): string {
  const m = n.meta as { label?: unknown };
  if (typeof m.label === 'string' && m.label) return m.label;
  return KIND_LABELS[n.kind];
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toISOString().slice(0, 10);
}

function relativeDays(iso: string, now = new Date()): string {
  const d = new Date(iso);
  const diff = Math.round((d.getTime() - now.getTime()) / 86_400_000);
  if (diff === 0) return 'idag';
  if (diff > 0) return `om ${diff} dagar`;
  if (diff === -1) return 'igår';
  return `${Math.abs(diff)} dagar sedan`;
}

export function ContractsInProgressPage() {
  usePageTitle('Avtal under bearbetning');
  // Default to "Aktuella" — the items that need action now. Previously
  // defaulted to "Kommande" but that buried the urgent stuff under a
  // (usually much larger) list of future reminders.
  const [window, setWindow] = useState<ContractReminderWindow>('due');

  const q = useTenantQuery(
    'notifications.list_grouped_by_contract',
    (w) =>
      tenantApi.notifications.list_grouped_by_contract({
        window: w as ContractReminderWindow,
        limit: 100,
      }),
    [window],
    { revalidateOnFocus: true },
  );

  // Per-tab distinct-contract counts. One round-trip on page load (and on
  // focus) so each tab label can carry its own "(N)" without nudging the
  // active-tab payload around.
  const countsQ = useTenantQuery(
    'notifications.counts_by_window',
    () => tenantApi.notifications.counts_by_window(),
    [],
    { revalidateOnFocus: true },
  );

  const rows = q.data?.contracts ?? [];
  const total = q.data?.total ?? 0;
  const activeWindow = WINDOWS.find((w) => w.id === window)!;
  const counts = countsQ.data ?? { upcoming: 0, due: 0, past: 0 };

  return (
    <div className="space-y-8 max-w-5xl">
      <header className="border-b border-stone-200 pb-6">
        <Link
          to="/"
          className="inline-flex items-center gap-1 text-xs text-stone-500 hover:text-stone-800"
        >
          <ArrowLeft className="h-3 w-3" />
          Tillbaka till översikt
        </Link>
        <h1 className="font-display text-3xl font-light tracking-tight mt-3 text-stone-900">
          Avtal under bearbetning
        </h1>
        <p className="text-sm text-stone-500 mt-2 max-w-2xl">
          Avtal sorterade efter nästa påminnelse. Varje rad visar avtalets
          första påminnelse i valt urval; växla mellan kommande, aktuella
          (nu) och redan hanterade.
        </p>
      </header>

      <div className="flex items-center gap-1 border-b border-stone-200">
        {WINDOWS.map((w) => {
          const active = w.id === window;
          const n = counts[w.id];
          return (
            <button
              key={w.id}
              type="button"
              onClick={() => setWindow(w.id)}
              className={
                'relative -mb-px px-4 py-2 text-sm transition-colors ' +
                (active
                  ? 'border-b-2 font-medium text-stone-900'
                  : 'border-b-2 border-transparent text-stone-500 hover:text-stone-800')
              }
              style={active ? { borderColor: HB, color: HB } : undefined}
            >
              {w.label}
              {!countsQ.isLoading && (
                <span
                  className={
                    'ml-1.5 text-xs ' +
                    (active ? 'text-stone-500' : 'text-stone-400')
                  }
                >
                  ({n})
                </span>
              )}
            </button>
          );
        })}
        <div className="ml-auto text-xs text-stone-400">
          {!q.isLoading && total > 0 && `${total} avtal`}
        </div>
      </div>

      {q.isLoading && (
        <div className="text-xs text-stone-400">Laddar…</div>
      )}

      {q.error && !q.isLoading && (
        <div className="rounded border border-red-300 bg-red-50 p-3 text-sm text-red-700">
          {q.error.message}
        </div>
      )}

      {!q.isLoading && !q.error && rows.length === 0 && (
        <div className="rounded border border-dashed border-stone-300 bg-stone-50 p-10 text-center">
          <p className="text-sm text-stone-700">{activeWindow.empty}</p>
        </div>
      )}

      {!q.isLoading && rows.length > 0 && (
        <ul className="divide-y divide-stone-100 rounded border border-stone-200 bg-white">
          {rows.map((r) => (
            <li key={r.contractId}>
              <Link
                to={`/contracts/${r.contractId}/edit?tab=paminnelser`}
                className="grid grid-cols-[1fr_auto] items-start gap-4 px-4 py-3 hover:bg-stone-50"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="truncate text-sm font-medium text-stone-900">
                      {r.contractTitle || 'Avtal utan titel'}
                    </span>
                    <span
                      className={
                        'rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider ' +
                        (CONTRACT_STATUS_CLASS[r.contractStatus] ??
                          'bg-stone-100 text-stone-700')
                      }
                    >
                      {CONTRACT_STATUS_LABEL[r.contractStatus] ?? r.contractStatus}
                    </span>
                  </div>
                  <div className="mt-1 flex items-center gap-2 text-xs text-stone-500">
                    <span className="font-medium text-stone-700">
                      {rowTitle(r)}
                    </span>
                    {r.reminderCount > 1 && (
                      <span className="text-[11px] text-stone-400">
                        +{r.reminderCount - 1} fler{' '}
                        {window === 'past'
                          ? 'hanterade'
                          : 'i den här gruppen'}
                      </span>
                    )}
                  </div>
                </div>
                <div className="text-right text-xs">
                  <DateCell row={r} window={window} />
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function DateCell({
  row,
  window,
}: {
  row: ContractWithFirstReminder;
  window: ContractReminderWindow;
}) {
  if (window === 'past') {
    const ack = row.acknowledgedAt;
    return (
      <div>
        <div className="text-stone-700">{ack ? formatDate(ack) : '—'}</div>
        <div className="text-stone-400">
          hanterad {ack ? relativeDays(ack) : ''}
        </div>
      </div>
    );
  }

  const now = Date.now();
  const due = new Date(row.fireAt).getTime() <= now;
  return (
    <div>
      <div className="text-stone-700">{formatDate(row.fireAt)}</div>
      {due ? (
        <span className="mt-0.5 inline-block rounded bg-amber-100 px-1.5 py-0.5 font-medium text-amber-800">
          visas nu
        </span>
      ) : (
        <div className="text-stone-400">{relativeDays(row.fireAt)}</div>
      )}
    </div>
  );
}
